/**
 * MODULE HOME — level 1 of the grammar (09 §1.4). `/c/{company_uid}/m/{module}`.
 *
 * It lists this module's PARENT SCREENS. Nothing here is a toolbar button whose label is a noun,
 * because 09 §1.3 is binding and it is the rule that gets violated: a noun is a screen, a verb is
 * an action. "ניירות עבודה" is a screen. "נעילת שנה" is an action.
 *
 * Only `company_management` and `billing` are `active` in the module catalogue. Every other module
 * is `planned` and renders §5.8's written state rather than an invented screen.
 */

import { useEffect } from 'react';
import { useLocale } from '../i18n/LocaleProvider';
import type { TextKey } from '../i18n/catalogue';
import { AppShell } from '../shell/AppShell';
import { useDeclareScreenContext, useWorkContext } from '../shell/ContextProvider';
import { StateBlock } from '../components/StateBlock';
import { DenialNotice } from '../components/DenialNotice';
import { routes } from '../routes';

export interface ModuleHomeScreenProps {
  readonly companyUid: string;
  readonly moduleCode: string;
}

/**
 * The parent screens each ACTIVE module offers. Deliberately short: this is the whole of what
 * exists today. Nothing is listed here that does not have a module code behind it.
 */
const PARENT_SCREENS: Readonly<Record<string, readonly { slug: string; titleKey: TextKey }[]>> = {
  company_management: [
    { slug: 'companies', titleKey: 'nav.companies' },
    { slug: 'sources', titleKey: 'nav.sources' },
  ],
  billing: [{ slug: 'subscription', titleKey: 'nav.subscription' }],
};

export function ModuleHomeScreen({ companyUid, moduleCode }: ModuleHomeScreenProps): JSX.Element {
  const { t } = useLocale();
  const ctx = useWorkContext();
  const { setCompanyByUid, setModuleByCode, setEntityLabel } = useDeclareScreenContext();

  useEffect(() => {
    setCompanyByUid(companyUid);
    setModuleByCode(moduleCode);
    setEntityLabel(null);
  }, [companyUid, moduleCode, setCompanyByUid, setModuleByCode, setEntityLabel]);

  const back = { labelHe: t('nav.companyHome'), href: routes.companyHome(companyUid) };
  const entitlement = ctx.modules.find((m) => m.moduleCode === moduleCode) ?? null;

  if (entitlement === null) {
    return (
      <AppShell
      /* Every string here goes through `t()`; `translatedScreens.test.ts` fails the build
         if one Hebrew literal survives, which is what makes this a claim and not a note. */
      translated titleHe={t('module.unknownTitle')} backToParent={back}>
        <StateBlock
          titleHe={t('module.notFoundTitle')}
          bodyHe={t('module.notFoundBody')}
          nextStep={<a className="btn" href={back.href}>{`${t('action.backTo')} ${back.labelHe}`}</a>}
        />
      </AppShell>
    );
  }

  if (!entitlement.available) {
    return (
      <AppShell
      /* Every string here goes through `t()`; `translatedScreens.test.ts` fails the build
         if one Hebrew literal survives, which is what makes this a claim and not a note. */
      translated titleHe={entitlement.displayNameHe} backToParent={back}>
        {/*
          The server decided. This renders its decision — it does not re-derive one.
          O-3: no pricing and no upgrade button in a "not subscribed" message.
        */}
        <DenialNotice
          deny={{
            error: 'not_permitted',
            ...(entitlement.unavailableReason ? { reason: entitlement.unavailableReason } : {}),
            requestUid: '',
          }}
        />
      </AppShell>
    );
  }

  if (entitlement.catalogueStatus !== 'active') {
    return (
      <AppShell
      /* Every string here goes through `t()`; `translatedScreens.test.ts` fails the build
         if one Hebrew literal survives, which is what makes this a claim and not a note. */
      translated titleHe={entitlement.displayNameHe} backToParent={back}>
        <StateBlock
          titleHe={t('state.unavailable')}
          bodyHe={t('module.plannedBody')}
          tone="unavailable"
          nextStep={<a className="btn" href={back.href}>{`${t('action.backTo')} ${back.labelHe}`}</a>}
        />
      </AppShell>
    );
  }

  const parents = PARENT_SCREENS[moduleCode] ?? [];

  return (
    <AppShell
      /* Every string here goes through `t()`; `translatedScreens.test.ts` fails the build
         if one Hebrew literal survives, which is what makes this a claim and not a note. */
      translated titleHe={entitlement.displayNameHe} backToParent={back}>
      <section className="tiles">
        {parents.map((p) => (
          // Nouns. Each one is a parent screen with its own address, not a toolbar button.
          <article key={p.slug} className="tile">
            <h2 className="tile__title">{t(p.titleKey)}</h2>
            <a className="btn btn--nav" href={routes.parentScreen(companyUid, moduleCode, p.slug)}>
              {t('action.open')}
            </a>
          </article>
        ))}
      </section>
    </AppShell>
  );
}
